import type { DetectorResult } from "../core/types.js";
import type { XPost } from "../sources/x.js";

/**
 * Cadence detector.
 *
 * Autonomous agents post at regular intervals, at all hours of the day, and
 * show no business-hours bias. Humans cluster posts in their waking hours
 * and go quiet during sleep.
 *
 * Signals analyzed:
 *  - Hour-of-day distribution entropy (high entropy = agent-like)
 *  - Business-hours ratio (0.4-0.8 is typical for humans, <0.3 for agents)
 *  - Interval-between-posts standard deviation (agents = low variance)
 *  - Sleep-window detection (5am-9am local dead zone = human)
 */
export function cadenceDetector(posts: XPost[]): DetectorResult {
  const notes: string[] = [];

  if (posts.length < 20) {
    notes.push(`only ${posts.length} posts available, low confidence`);
    return {
      detector: "cadence",
      score: 50,
      samples: posts.length,
      notes,
      runtime_ms: 0,
    };
  }

  // Bucket by hour of day (UTC).
  const hourBuckets = new Array(24).fill(0);
  for (const p of posts) {
    const hour = new Date(p.created_at * 1000).getUTCHours();
    hourBuckets[hour]++;
  }

  // Shannon entropy of the distribution (0 = all same hour, log2(24) = uniform).
  const total = posts.length;
  let entropy = 0;
  for (const count of hourBuckets) {
    if (count > 0) {
      const p = count / total;
      entropy -= p * Math.log2(p);
    }
  }
  const entropyScore = (entropy / Math.log2(24)) * 100;
  notes.push(`hour entropy: ${entropy.toFixed(2)} bits (${entropyScore.toFixed(0)}/100)`);

  // Business hours bias (9:00 - 18:00 UTC, adjust if you have timezone).
  const businessHours = hourBuckets.slice(9, 18).reduce((a, b) => a + b, 0);
  const businessRatio = businessHours / total;
  notes.push(`business-hours ratio: ${(businessRatio * 100).toFixed(1)}%`);

  // Sleep window detection (4am - 8am UTC by default).
  const sleepWindow = hourBuckets.slice(4, 8).reduce((a, b) => a + b, 0);
  const sleepRatio = sleepWindow / total;
  const hasDeadZone = sleepRatio < 0.05;
  notes.push(
    hasDeadZone
      ? `sleep window detected (${(sleepRatio * 100).toFixed(1)}% posts in 4-8am)`
      : `no sleep window (${(sleepRatio * 100).toFixed(1)}% posts in 4-8am)`
  );

  // Interval variance.
  const sorted = [...posts].sort((a, b) => a.created_at - b.created_at);
  const intervals: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    intervals.push(sorted[i]!.created_at - sorted[i - 1]!.created_at);
  }
  const meanInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  const variance =
    intervals.reduce((a, b) => a + (b - meanInterval) ** 2, 0) /
    intervals.length;
  const cv = Math.sqrt(variance) / meanInterval; // coefficient of variation
  notes.push(`interval CV: ${cv.toFixed(2)} (lower = more regular = agent-like)`);

  // Aggregate the four sub-signals into a 0-100 score.
  let score = 0;
  score += entropyScore * 0.4;
  score += (1 - Math.abs(businessRatio - 0.15)) * 100 * 0.25; // 15% business hours is agent-like baseline
  score += (hasDeadZone ? 0 : 100) * 0.2;
  score += Math.max(0, 100 - cv * 30) * 0.15;

  return {
    detector: "cadence",
    score: Math.round(Math.max(0, Math.min(100, score))),
    samples: posts.length,
    notes,
    runtime_ms: 0,
  };
}
